import {
  NeedDto,
  NeedUpdateDto,
  NeedUpdateKey,
  NeedsIssueDto,
} from '@act/shared/data-transfer-objects';
import HtmlDisplayer from 'react-components/components/HtmlDisplayer';
import { selectNeedsMetadata } from 'redux/slices/metaSlice.slice';
import { userAppSelectors } from 'redux/slices/usersSlice.slice';
import { useAppSelector } from 'redux/storeHooks';
import {
  StyledHistoryEntry,
  StyledHistoryEntryDescription,
  StyledHistoryEntrySubheader,
  getHistoryEntryDateLine,
} from './HistoryDisplay';

interface UpdateHistoryEntryProps {
  need: NeedDto;
  needUpdate: NeedUpdateDto;
  detailMode?: boolean;
}

const updateKeyToLabel = (needUpdate: NeedUpdateDto, need?: NeedDto) => {
  switch (needUpdate?.key) {
    case NeedUpdateKey?.IDENTIFIED_AT:
      return need?.assignedSurveyId
        ? `Need identified through the ${
            need?.assignedSurvey?.survey?.displayName ||
            need?.assignedSurvey?.survey?.name
          } survey`
        : 'Need identified';
    case NeedUpdateKey?.FULFILLED_AT:
      return 'Need fulfilled';
    case NeedUpdateKey?.ABANDONED_AT:
      return 'Stopped pursuing';
    case NeedUpdateKey?.EXPIRES_AT:
      return 'Time window extended';
    case NeedUpdateKey?.REASON:
      return 'Need reason modified';
    case NeedUpdateKey?.ISSUE:
      return 'Need issue updated';
    default:
      return 'Unrecognized update';
  }
};

const getDescription = (
  need: NeedDto,
  needUpdate: NeedUpdateDto,
  issues?: NeedsIssueDto[]
) => {
  switch (needUpdate?.key) {
    case NeedUpdateKey?.IDENTIFIED_AT:
      return <HtmlDisplayer innerHtml={need.originalReason} />;
    case NeedUpdateKey?.REASON:
      return <HtmlDisplayer innerHtml={needUpdate.newValue} />;
    case NeedUpdateKey?.ISSUE:
      return (
        <HtmlDisplayer
          innerHtml={`<p>
              ${
                issues.find(issue => issue.id === +needUpdate.originalValue)
                  ?.label || needUpdate?.originalValue
              } =>
              ${
                issues.find(issue => issue.id === +needUpdate.newValue)
                  ?.label || needUpdate?.newValue
              }
            </p>`}
        />
      );
    default:
      return null;
  }
};

const UpdateHistoryEntry = ({
  need,
  needUpdate,
  detailMode,
}: UpdateHistoryEntryProps) => {
  const issues = useAppSelector(selectNeedsMetadata)?.data?.issues;
  const creatorName = userAppSelectors.useSelectUserById(
    needUpdate?.creatorId
  )?.name;

  const descriptionNode =
    detailMode && getDescription(need, needUpdate, issues);

  return (
    <StyledHistoryEntry
      header={updateKeyToLabel(needUpdate, need)}
      content={
        <div>
          <StyledHistoryEntrySubheader>
            {getHistoryEntryDateLine(needUpdate?.createdAt, creatorName)}
          </StyledHistoryEntrySubheader>
          <StyledHistoryEntryDescription>
            {needUpdate.comment}
          </StyledHistoryEntryDescription>
        </div>
      }
      expandedContent={
        descriptionNode && (
          <StyledHistoryEntryDescription>
            <div>{descriptionNode}</div>
          </StyledHistoryEntryDescription>
        )
      }
    />
  );
};

export default UpdateHistoryEntry;
